import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import communityApi from '@/apis/community';
import * as InboxViewAPI from '@/apis/community-inbox-view';
import { getUnreadCountQueryParams } from '@/app/community/utils/store-utils';
import { logger } from '@/utils/logger';

export const useCommunityInboxViewStore = defineStore('communityInboxView', () => {
  const inboxViewList = ref([]);
  const activeView = ref(null);
  const activeViewFilters = computed(() => activeView.value?.filterDetails ?? {});
  const activeViewBrands = computed(() => activeView.value?.viewBrandDetails ?? []);
  const activeViewUnreadCount = ref(0);

  const pending = ref({
    getActiveView: false,
    deleteView: false,
    updateActiveView: false,
    createView: false,
  });

  async function getInboxViews() {
    try {
      const res = await InboxViewAPI.getInboxViews();
      inboxViewList.value = res.data;
    } catch (error) {
      inboxViewList.value = [];
      logger.error('Error fetching inbox view list', {}, error);
    }
  }

  async function getActiveInboxView(viewId) {
    pending.value.getActiveView = true;
    activeView.value = null;
    try {
      const res = await InboxViewAPI.getInboxView(viewId);
      activeView.value = res.data;
      pending.value.getActiveView = false;
    } catch (error) {
      pending.value.getActiveView = false;
      const viewExists = inboxViewList.value.some((t) => t.id === viewId);
      if ([404, 401].includes(error?.response?.status) && viewExists) {
        // refresh view list if the active view is not available
        await getInboxViews();
      }
      throw error;
    }
  }

  function clearActiveInboxView() {
    activeView.value = null;
  }

  async function deleteCustomInboxView(viewId) {
    pending.value.deleteView = true;
    try {
      await InboxViewAPI.deleteCustomInboxView(viewId);
      inboxViewList.value = inboxViewList.value.filter((v) => v.id !== viewId);
      if (activeView.value?.id === viewId) {
        activeView.value = null;
      }
      pending.value.deleteView = false;
      await getInboxViews();
    } catch (error) {
      pending.value.deleteView = false;
      logger.error('Error deleting inbox view', { viewId }, error);
      throw error;
    }
  }

  async function leaveCustomInboxView(viewId) {
    await InboxViewAPI.leaveCustomInboxView(viewId);
    inboxViewList.value = inboxViewList.value.filter((v) => v.id !== viewId);
    if (activeView.value?.id === viewId) {
      activeView.value = null;
    }
    await getInboxViews();
  }

  async function createCustomInboxView({ filterDetails, name, userAccessGrants, accessType }) {
    pending.value.createView = true;
    try {
      const accessGrants = userAccessGrants?.map((grant) => ({
        userId: grant.userId,
        role: grant.role,
        // Exclude name, avatarUrl and other UI-only fields
      }));

      const payload = omitBy(
        {
          filterDetails,
          name,
          userAccessGrants: accessGrants,
          accessType,
        },
        isNil,
      );
      const res = await InboxViewAPI.createInboxView(payload);

      // Add the new view to the list
      inboxViewList.value.unshift(res.data);
      pending.value.createView = false;
      return res.data;
    } catch (error) {
      pending.value.createView = false;
      throw error;
    }
  }

  async function updateCustomInboxView({
    viewId,
    filterDetails,
    name,
    userAccessGrants,
    accessType,
  }) {
    pending.value.updateActiveView = true;
    const updatedViewId = viewId ?? activeView.value?.id;
    try {
      const payload = omitBy({ filterDetails, name, userAccessGrants, accessType }, isNil);
      const res = await InboxViewAPI.updateInboxView(updatedViewId, payload);
      if (updatedViewId === activeView.value?.id) {
        activeView.value = res.data;
      }
      const viewIndex = inboxViewList.value.findIndex((v) => v.id === updatedViewId);
      if (viewIndex > -1) {
        inboxViewList.value[viewIndex] = res.data;
      }
      pending.value.updateActiveView = false;
    } catch (error) {
      pending.value.updateActiveView = false;
      const viewExists = inboxViewList.value.some((t) => t.id === updatedViewId);
      if ([404, 401].includes(error?.response?.status) && viewExists) {
        // refresh view list if the active view is not available
        if (updatedViewId === activeView.value?.id) {
          clearActiveInboxView();
        }
        await getInboxViews();
      }
      throw error;
    }
  }

  async function getActiveViewUnreadCount(filters) {
    const payload = {
      ...getUnreadCountQueryParams(filters),
      isView: true,
    };

    const res = await communityApi.getUnreadCommunityInteractionCountsV2(payload);
    activeViewUnreadCount.value = res.data?.INBOX ?? 0;
  }

  return {
    inboxViewList,
    activeView,
    activeViewFilters,
    activeViewBrands,
    pending,
    getInboxViews,
    getActiveInboxView,
    clearActiveInboxView,
    deleteCustomInboxView,
    leaveCustomInboxView,
    createCustomInboxView,
    updateCustomInboxView,
    activeViewUnreadCount,
    getActiveViewUnreadCount,
  };
});
